import React, { useState, useEffect } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { Phone, Mail, Twitter, Facebook, Instagram, Youtube, Menu, X, Heart, LogOut } from "lucide-react";
import { Button } from "./ui/button";
import { getUser, clearAuth } from "../lib/api";
import Chatbot from "./Chatbot";

const NAV = [
  { to: "/", label: "Home" },
  { to: "/what-we-do", label: "What We Do" },
  { to: "/csr", label: "CSR" },
  { to: "/career", label: "Career" },
  { to: "/gallery", label: "Gallery" },
  { to: "/documents", label: "Documents" },
  { to: "/about", label: "About" },
  { to: "/contact", label: "Contact" },
];

const SCROLL_TEXT =
  "WELCOME TO ADOPT HELPLESS CHILDREN CHARITABLE TRUST • Reg. No: 250501328202 • NGO DARPAN ID: BR/2025/0867253 • PAN: AAMTA0405B • 80G Reg: AAMTA0405BF2025101 • 12A Reg: AAMTA0405BE2025101 • CSR Reg No: CSR00103199 • E-anudan: BR/00049949 • SBI A/C No: 44703110576 • IFSC: SBIN0018600 •  ";

function TricolourHeader() {
  return (
    <div className="w-full" data-testid="tricolour-header">
      <div style={{ background: "#FF9933" }} className="h-[10px]" />
      <div className="bg-white h-9 border-y border-[#eae5d9] marquee-wrap">
        <div className="marquee-track">
          <span className="text-[13px] md:text-sm font-semibold text-[#000080] px-6 py-2">{SCROLL_TEXT.repeat(2)}</span>
          <span className="text-[13px] md:text-sm font-semibold text-[#000080] px-6 py-2">{SCROLL_TEXT.repeat(2)}</span>
        </div>
      </div>
      <div style={{ background: "#138808" }} className="h-[10px]" />
    </div>
  );
}

function TopBar() {
  const user = getUser();
  return (
    <div className="bg-[#F3EFE7] text-[#4A4A4A] text-xs md:text-sm">
      <div className="max-w-7xl mx-auto px-4 md:px-6 py-2 flex flex-wrap items-center justify-between gap-2">
        <div className="flex flex-wrap items-center gap-4">
          <a href="tel:+918002485087" className="flex items-center gap-1.5 hover:text-[#FF9933]" data-testid="topbar-phone">
            <Phone className="w-3.5 h-3.5" strokeWidth={1.7} />
            (+91) 8002485087 / 7762864168
          </a>
          <a href="mailto:Support@adopthelplesschildren.com" className="flex items-center gap-1.5 hover:text-[#FF9933]" data-testid="topbar-email">
            <Mail className="w-3.5 h-3.5" strokeWidth={1.7} />
            Support@adopthelplesschildren.com
          </a>
        </div>
        <div className="flex items-center gap-3">
          <a href="#" aria-label="Twitter" data-testid="social-twitter" className="hover:text-[#000080]"><Twitter className="w-4 h-4" strokeWidth={1.6} /></a>
          <a href="#" aria-label="Facebook" data-testid="social-facebook" className="hover:text-[#000080]"><Facebook className="w-4 h-4" strokeWidth={1.6} /></a>
          <a href="#" aria-label="Instagram" data-testid="social-instagram" className="hover:text-[#000080]"><Instagram className="w-4 h-4" strokeWidth={1.6} /></a>
          <a href="#" aria-label="YouTube" data-testid="social-youtube" className="hover:text-[#000080]"><Youtube className="w-4 h-4" strokeWidth={1.6} /></a>
          <span className="h-3 w-px bg-[#d9d3c3]" />
          {user ? (
            <>
              <span className="text-[#000080] font-semibold" data-testid="topbar-username">{user.name}</span>
              {user.role === "admin" && (
                <Link to="/admin" className="text-[#FF9933] font-semibold" data-testid="topbar-admin">Admin</Link>
              )}
              {(user.role === "admin" || user.role === "accountant") && (
                <Link to="/accounting" className="text-[#138808] font-semibold" data-testid="topbar-accounting">Accounts</Link>
              )}
              {(user.role === "admin" || user.role === "accountant") && (
                <Link to="/admin/members" className="text-[#000080] font-semibold" data-testid="topbar-members">Members</Link>
              )}
              <button
                onClick={() => { clearAuth(); window.location.href = "/"; }}
                className="flex items-center gap-1 hover:text-[#FF9933]"
                data-testid="topbar-logout"
              >
                <LogOut className="w-3.5 h-3.5" /> Logout
              </button>
            </>
          ) : (
            <>
              <Link to="/accounting/login" className="hover:text-[#138808]" data-testid="topbar-accountant-login">Accountant</Link>
              <Link to="/login" className="hover:text-[#FF9933]" data-testid="topbar-login">Login</Link>
              <Link to="/register" className="hover:text-[#FF9933]" data-testid="topbar-register">Register</Link>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function MainNav() {
  const [open, setOpen] = useState(false);
  return (
    <header className="bg-white/90 backdrop-blur-md sticky top-0 z-40 border-b border-[#eae5d9]">
      <div className="max-w-7xl mx-auto px-4 md:px-6 h-20 flex items-center justify-between gap-6">
        <Link to="/" className="flex items-center gap-3" data-testid="brand-logo">
          <img src="/logo.png" alt="AHCCT Logo" className="w-14 h-14 rounded-full object-cover border-2 border-[#000080]/10" />
          <div className="leading-tight">
            <div className="serif text-[#000080] text-lg md:text-xl font-semibold">Adopt Helpless Children</div>
            <div className="text-[10px] md:text-xs uppercase tracking-[0.18em] text-[#737373]">Charitable Trust</div>
          </div>
        </Link>

        <nav className="hidden lg:flex items-center gap-7">
          {NAV.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              data-testid={`nav-${n.label.toLowerCase().replace(/\s+/g, "-")}`}
              className={({ isActive }) =>
                `text-sm font-semibold tracking-wide hover:text-[#FF9933] ${isActive ? "text-[#FF9933]" : "text-[#1A1A1A]"}`
              }
              end={n.to === "/"}
            >
              {n.label}
            </NavLink>
          ))}
        </nav>

        <div className="hidden lg:block">
          <Link to="/donate" data-testid="nav-donate-cta">
            <Button className="rounded-full bg-[#FF9933] hover:bg-[#E68A2E] text-white px-6 btn-glow">
              <Heart className="w-4 h-4 mr-2" /> Donate Now
            </Button>
          </Link>
        </div>

        <button onClick={() => setOpen(!open)} className="lg:hidden" aria-label="Menu" data-testid="mobile-menu-toggle">
          {open ? <X /> : <Menu />}
        </button>
      </div>

      {open && (
        <div className="lg:hidden border-t border-[#eae5d9] bg-white">
          <div className="px-4 py-3 flex flex-col gap-2">
            {NAV.map((n) => (
              <NavLink key={n.to} to={n.to} onClick={() => setOpen(false)} className="py-2 text-sm font-semibold" data-testid={`mnav-${n.label.toLowerCase().replace(/\s+/g, "-")}`}>
                {n.label}
              </NavLink>
            ))}
            <Link to="/donate" onClick={() => setOpen(false)}>
              <Button className="w-full rounded-full bg-[#FF9933] hover:bg-[#E68A2E] text-white" data-testid="mnav-donate">Donate Now</Button>
            </Link>
          </div>
        </div>
      )}
    </header>
  );
}

function Footer() {
  return (
    <footer className="bg-[#0b0b2a] text-white mt-20">
      <div className="max-w-7xl mx-auto px-6 py-16 grid md:grid-cols-4 gap-10">
        <div>
          <div className="serif text-2xl mb-3 text-white">Adopt Helpless <br/>Children Trust</div>
          <p className="text-sm text-white/70 leading-relaxed">
            A registered 80G & 12A charitable trust working to uplift underprivileged communities through education, healthcare, and empowerment.
          </p>
        </div>
        <div>
          <div className="text-sm font-bold uppercase tracking-wider mb-3 text-[#FF9933]">Quick Links</div>
          <ul className="space-y-2 text-sm text-white/80">
            {NAV.map(n => <li key={n.to}><Link to={n.to} className="hover:text-[#FF9933]">{n.label}</Link></li>)}
            <li><Link to="/donate" className="hover:text-[#FF9933]">Donate</Link></li>
          </ul>
        </div>
        <div>
          <div className="text-sm font-bold uppercase tracking-wider mb-3 text-[#FF9933]">Registrations</div>
          <ul className="space-y-1.5 text-xs text-white/70">
            <li>Reg No: 250501328202</li>
            <li>NGO DARPAN: BR/2025/0867253</li>
            <li>PAN: AAMTA0405B</li>
            <li>80G: AAMTA0405BF2025101</li>
            <li>12A: AAMTA0405BE2025101</li>
            <li>CSR: CSR00103199</li>
          </ul>
        </div>
        <div>
          <div className="text-sm font-bold uppercase tracking-wider mb-3 text-[#FF9933]">Reach Us</div>
          <ul className="space-y-2 text-sm text-white/80">
            <li>+91 8002485087 / 7762864168</li>
            <li>Support@adopthelplesschildren.com</li>
            <li>SBI A/C: 44703110576</li>
            <li>IFSC: SBIN0018600</li>
          </ul>
        </div>
      </div>
      <div className="border-t border-white/10 text-center text-xs text-white/50 py-4">
        © {new Date().getFullYear()} Adopt Helpless Children Charitable Trust • All donations eligible for 80G tax benefit
      </div>
    </footer>
  );
}

export default function Layout({ children }) {
  return (
    <div className="App paper-bg min-h-screen flex flex-col">
      <TricolourHeader />
      <TopBar />
      <MainNav />
      <main className="flex-1">{children}</main>
      <Footer />
      <Chatbot />
    </div>
  );
}
